import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const services = [
  {
    title: "Elevator Design & Planning",
    description: "Every building has unique transportation requirements. Our specialists conduct detailed technical evaluations to determine the most efficient elevator configuration for each project.",
    includes: [
      "Traffic flow analysis",
      "System capacity evaluation",
      "Equipment selection",
      "Technical drawings and specifications",
      "Compliance with safety and engineering standards"
    ],
    icon: "Layout"
  },
  {
    title: "Elevator Installation",
    description: "VASTECH provides professional installation services executed by experienced technicians and engineers. Our installation process prioritizes accuracy, safety, and strict adherence to engineering specifications.",
    includes: [
      "Elevator shaft preparation and setup",
      "Mechanical component installation",
      "Control system integration",
      "Electrical system configuration",
      "System calibration and testing"
    ],
    icon: "PlusSquare"
  },
  {
    title: "Elevator Modernization",
    description: "Older elevator systems often require upgrades to improve performance, safety, and energy efficiency. Our modernization services replace outdated components with advanced technologies.",
    includes: [
      "Control system upgrades",
      "Drive system replacement",
      "Cabin refurbishment",
      "Safety component improvements",
      "Energy-efficient technologies"
    ],
    icon: "RefreshCw"
  },
  {
    title: "Preventive Maintenance",
    description: "Regular maintenance is critical for maintaining elevator safety and reliability. Our preventive maintenance programs ensure that systems operate smoothly while reducing the risk of unexpected breakdowns.",
    includes: [
      "Routine inspections",
      "Component lubrication and adjustment",
      "Safety system testing",
      "Performance monitoring",
      "Early fault detection"
    ],
    icon: "Shield"
  },
  {
    title: "Emergency Repair Services",
    description: "Unexpected technical issues can disrupt building operations. Our technical support team provides rapid response services to diagnose and resolve elevator faults efficiently.",
    includes: [
      "Rapid response services",
      "Diagnostics and fault resolution",
      "Focus on restoring safe operation",
      "As quickly as possible"
    ],
    icon: "AlertTriangle"
  }
];

const ElevatorServices = () => {
  return (
    <section className="py-24 bg-slate-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-sm font-bold uppercase tracking-[0.4em] text-[#91756A] mb-4"
          >
            Our Elevator Services
          </motion.h2>
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-4xl md:text-5xl font-bold text-[#4D4C4A] italic tracking-tighter"
          >
            End-to-End Vertical Solutions.
          </motion.h3>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              className="bg-white p-10 shadow-sm border border-slate-100 hover:shadow-xl transition-all duration-500 group flex flex-col"
            >
              <div className="w-14 h-14 bg-[#4D4C4A] flex items-center justify-center shrink-0 mb-8 group-hover:bg-[#91756A] transition-colors duration-300">
                <Icon name={service.icon} size={28} className="text-white" />
              </div>
              <h4 className="text-2xl font-bold text-[#4D4C4A] italic tracking-tight uppercase mb-4">
                {service.title}
              </h4>
              <p className="text-slate-500 leading-relaxed mb-8 flex-grow">
                {service.description}
              </p>

              <div className="space-y-3">
                {service.includes.map((item, itemIndex) => (
                  <div key={itemIndex} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#91756A] rounded-full mt-1.5"></div>
                    <span className="text-sm text-slate-600 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ElevatorServices;